import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { cn } from "@/shared/lib";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import type { AnalysisDeath } from "../../model/Analysis";
import { AssetIcon } from "../AssetIcon";

const TITLE = msg({ id: "analysis.deaths.list", message: "Every death" });
const NOT_HERO = msg({ id: "analysis.deaths.not_hero", message: "Creeps and towers" });
const LANE = msg({ id: "analysis.deaths.tag.lane", message: "In lane" });
const OUTNUMBERED = msg({
  id: "analysis.deaths.tag.outnumbered",
  message: "{mine} vs {theirs}, {seconds} s",
});
const ALONE = msg({ id: "analysis.deaths.tag.alone", message: "No allies for {seconds} s" });
const FIGHT = msg({ id: "analysis.deaths.tag.fight", message: "{mine} vs {theirs}" });
const RESPAWN = msg({ id: "analysis.deaths.respawn", message: "respawn {seconds} s" });
const LOST = msg({
  id: "analysis.deaths.lost",
  message:
    "{seconds} s later {lost, select, mid_boss {enemies took the Mid Boss} other {a building fell}}",
});
const BAD_FIGHT = msg({
  id: "analysis.deaths.bad_fight",
  message: "Losing fight",
});

type Tag = { text: string; bad: boolean };

type DeathListProps = {
  deaths: readonly AnalysisDeath[];
};

// Каждая смерть строкой: когда, кто убил, в какой обстановке и чего стоила. Метки — из
// подробностей разбора: невыгодная драка (`fight`, как у находок) — красная, остальное —
// серое. Так смерть «один против пятерых» видна, даже если таких смертей мало для находки.
export const DeathList = ({ deaths }: DeathListProps) => {
  const { _ } = useLingui();
  const findHero = useAssetLookup("heroes");
  const tagsOf = (death: AnalysisDeath): Tag[] => {
    const mine = 1 + death.ally_hero_ids.length;
    const theirs = death.enemy_hero_ids.length;
    const tags: Tag[] = [];
    if (death.lane_phase) {
      tags.push({ text: _(LANE), bad: false });
    }
    if (death.fight === "outnumbered") {
      tags.push({
        text: _({ ...OUTNUMBERED, values: { mine, theirs, seconds: death.fight_s } }),
        bad: true,
      });
    } else if (death.fight === "alone") {
      tags.push({ text: _({ ...ALONE, values: { seconds: death.fight_s } }), bad: true });
    } else if (theirs > 0) {
      tags.push({ text: _({ ...FIGHT, values: { mine, theirs } }), bad: false });
    }
    return tags;
  };
  return (
    <div className="flex flex-col">
      <h4 className="pb-1.5 text-[13px] font-normal text-subtle">{_(TITLE)}</h4>
      <ol className="flex flex-col">
        {deaths.map((death, index) => {
          const killer = death.killer_hero_id === null ? null : findHero(death.killer_hero_id);
          const bad = death.fight !== null;
          const cost = [
            _({ ...RESPAWN, values: { seconds: death.dead_for_s } }),
            ...(death.lost !== null && death.lost_at_s !== null
              ? [
                  _({
                    ...LOST,
                    values: { seconds: death.lost_at_s - death.at_s, lost: death.lost },
                  }),
                ]
              : []),
          ].join(" · ");
          return (
            <li
              // Смерти отсортированы по времени и не меняются — индекс делает ключ уникальным.
              key={`${death.at_s}-${index}`}
              data-death={death.at_s}
              data-fight={death.fight ?? "none"}
              className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 border-t border-border py-2.5"
            >
              <span className="w-11 shrink-0 font-mono text-sm text-foreground">
                {formatGameTime(death.at_s)}
              </span>
              <span className="flex w-36 shrink-0 items-center gap-2">
                {killer === null ? (
                  <span
                    aria-hidden="true"
                    className="inline-flex size-[30px] items-center justify-center rounded-full bg-surface-raised text-subtle"
                  >
                    ×
                  </span>
                ) : (
                  <AssetIcon
                    asset={killer}
                    size={30}
                    decorative
                    className={cn(
                      "rounded-full border-2",
                      bad ? "border-bad" : "border-border-strong",
                    )}
                  />
                )}
                <span className="truncate text-sm text-foreground">
                  {killer?.name ?? _(NOT_HERO)}
                </span>
              </span>
              <span className="flex min-w-0 flex-[1_1_200px] flex-wrap gap-1.5">
                {bad && <span className="sr-only">{_(BAD_FIGHT)}: </span>}
                {tagsOf(death).map((tag) => (
                  <span
                    key={tag.text}
                    className={cn(
                      "inline-flex rounded-full border px-2.5 py-0.5 text-xs",
                      tag.bad
                        ? "border-bad/40 bg-bad/10 text-bad"
                        : "border-border bg-surface-raised text-muted",
                    )}
                  >
                    {tag.text}
                  </span>
                ))}
              </span>
              <span className="ml-auto text-right text-[13px] text-muted">{cost}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
